/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import EditRowFormFields from "./EditRowFormFields";
import Button, { Icons } from "../buttons/Button";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import { errorAlert, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import InlineLoader from "../loader/InlineLoader";
import {
  EDIT_ORG_PROFILE_TRANSLATIONS,
  EDIT_ROW_TRANSLATIONS,
  LOADING_TEXT_TRANSLATIONS,
  SIDEPANEL_GENERAL_TRANSLATIONS,
} from "../../translations/util";
import EditGroupFormButtons from "./EditGroupFormButtons";
import OrgContext from "../../store/orgContext";

interface IProps {
  setShowEditPanel?: any;
  panelName: string;
  esRef: string;
  initialOrgState?: any;
  initialOrgProfileState?: any;
  orgFormFields?: any;
  orgProfileFormFields?: any;
  header: string;
  onOrgUpdate?: any;
  onOrgProfileUpdate?: any;
  loadDataByRef?: any;
  fetchOrgData?: any;
  fetchOrgProfileData?: any;
  deleteModalItemName?: any;
  onDelete?: any;
  hasDeletePermission?: any;
  hasOrgEditPermission?: boolean;
  hasOrgProfileEditPermission?: boolean;
  hasOrgProfileViewPermission?: boolean;
  refreshTable?: any;
  showSmallSidepanel?: boolean;
  transformOrgProfileUpdateBody?: any;
  transformOrgUpdateBody?: any;
  transformResponseRow?: any;
  transformProfileResponseRow?: any;
}

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

const isFormInvalid = ({
  data,
  formFields,
}: {
  data: any;
  formFields: any;
}) => {
  if (!data) return true;
  const isEditFormValid = formFields
    .map((rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "NUMBER":
            return isInValidNumberForField(
              data[field.name],
              field.min,
              field.max
            );
          default:
            return false;
        }
      });
      return res.reduce((acc: any, el: any) => acc || el);
    })
    .reduce((acc: any, el: any) => acc || el);

  return isEditFormValid;
};

const EditOrg = ({
  setShowEditPanel,
  onDelete,
  esRef,
  panelName,
  initialOrgState,
  initialOrgProfileState,
  orgFormFields,
  orgProfileFormFields,
  header,
  onOrgUpdate,
  onOrgProfileUpdate,
  loadDataByRef,
  fetchOrgData,
  fetchOrgProfileData,
  deleteModalItemName,
  hasDeletePermission = false,
  hasOrgEditPermission = false,
  hasOrgProfileEditPermission = false,
  hasOrgProfileViewPermission = false,
  refreshTable,
  showSmallSidepanel = true,
  transformOrgProfileUpdateBody,
  transformOrgUpdateBody,
  transformResponseRow,
  transformProfileResponseRow,
}: IProps) => {
  const { token } = useContext(AuthContext);
  const { handleRefreshOrgList } = useContext(OrgContext);

  // local state to open/close Delete and Edit Modal
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openEditProfileModal, setOpenEditProfileModal] = useState(false);

  const [isOrgEditing, setIsOrgEditing] = useState(false);
  const [isOrgProfileEditing, setIsOrgProfileEditing] = useState(false);

  // local states to set/unset Loader for Org and Org Profile Sections
  const [orgDataLoading, setOrgDataLoading] = useState(false);
  const [orgProfileDataLoading, setOrgProfileDataLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  // local state to set/unset org data and org profile data
  const [orgData, setOrgData] = useState(initialOrgState);
  const [orgProfileData, setOrgProfileData] = useState(initialOrgProfileState);
  const [initialOrgData, setInitialOrgData] = useState(orgData);
  const [initialOrgProfileData, setInitialOrgProfileData] =
    useState(orgProfileData);

  // to set/unset error state
  const [hasOrgError, setHasOrgError] = useState(false);
  const [hasOrgProfileError, setHasOrgProfileError] = useState(false);

  // to fetch data of Org and Org-Profile simultaneously by Ref/Org-ID
  const getDataByRef = async (ref: string) => {
    if (ref && token) {
      setDataLoading(true);

      try {
        const response = await loadDataByRef({
          token,
          orgID: ref,
        });
        const [orgResponse, orgProfileResponse] = response;
        if (orgResponse.status === "fulfilled") {
          setOrgData(orgResponse.value);
          setInitialOrgData(orgResponse.value);
          if (orgProfileResponse.status === "fulfilled") {
            setOrgProfileData(orgProfileResponse.value);
            setInitialOrgProfileData(orgProfileResponse.value);
          } else {
            setHasOrgProfileError(true);
            if (hasOrgProfileViewPermission)
              throw new Error(orgProfileResponse.reason);
          }
        } else {
          if (orgProfileResponse.status === "fulfilled") {
            setOrgProfileData(orgProfileResponse.value);
            setInitialOrgProfileData(orgProfileResponse.value);
          } else {
            setHasOrgProfileError(true);
            if (hasOrgProfileViewPermission)
              throw new Error(orgProfileResponse.reason);
          }
          setHasOrgError(true);
          throw new Error(orgResponse.reason);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setDataLoading(false);
      }
    }
  };

  // to fetch data of Organisation by Ref/Org-ID
  const getOrgDataByRef = async (ref: string) => {
    if (ref && token) {
      setOrgDataLoading(true);
      try {
        const orgResponse = await fetchOrgData({
          token,
          orgID: ref,
        });
        setOrgData(transformResponseRow(orgResponse));
        setInitialOrgData(transformResponseRow(orgResponse));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setOrgDataLoading(false);
      }
    }
  };

  // to fetch data of Organisation Profile by Ref/Org-ID
  const getOrgProfileDataByRef = async (ref: string) => {
    if (ref && token) {
      setOrgProfileDataLoading(true);
      try {
        const profileResponse = await fetchOrgProfileData({
          token,
          orgID: ref,
        });
        setOrgProfileData(transformProfileResponseRow(profileResponse));
        setInitialOrgProfileData(transformProfileResponseRow(profileResponse));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setOrgProfileDataLoading(false);
      }
    }
  };

  // to update data of Organisation
  const handleOrgUpdate = async () => {
    setOrgDataLoading(true);
    try {
      await onOrgUpdate({
        body: transformOrgUpdateBody(orgData),
        token,
        ref: esRef,
      });
      if (refreshTable) {
        refreshTable();
      }
      getOrgDataByRef(esRef);
      handleRefreshOrgList({
        id: orgData["ORG-ID"],
        text: `${orgData["ORG-NAME"]} (${orgData["ORG-ID"]})`,
      });
      successAlert(EDIT_ROW_TRANSLATIONS.EDIT_SUCCESS_MESSAGE);
      setIsOrgEditing(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setOrgDataLoading(false);
    }
  };

  // to update data of Organisation Profile
  const handleOrgProfileUpdate = async () => {
    setOrgProfileDataLoading(true);
    try {
      await onOrgProfileUpdate({
        body: transformOrgProfileUpdateBody(orgProfileData),
        token,
        ref: esRef,
      });
      if (refreshTable) {
        refreshTable();
      }
      getOrgProfileDataByRef(esRef);
      successAlert(EDIT_ORG_PROFILE_TRANSLATIONS.orgProfileSucessMsg);
      setIsOrgProfileEditing(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setOrgProfileDataLoading(false);
    }
  };

  useEffect(() => {
    getDataByRef(esRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esRef]);

  return (
    <>
      <aside
        id="sidepanel"
        data-cy="editOrg-sidepanel"
        className={`sidepanel ${
          showSmallSidepanel
            ? "sidepanel-view small-sidepanel"
            : "sidepanel-edit"
        } fixed z-40 h-full transform transition-transform ease-in duration-150 bg-white`}
      >
        <div className="panel-header">
          <div className="panel-left-side">
            <Button
              kind="ghost"
              className="close-btn"
              icon={Icons.Close}
              onClick={() => setShowEditPanel()}
              dataCy="closeEditOrg-"
            />
            <h5 data-cy="editOrg-panelName"> {panelName} </h5>
          </div>
          <div className="panel-right-side tab-header-buttons">
            <Button
              kind="tertiary"
              className="w-sm"
              label={SIDEPANEL_GENERAL_TRANSLATIONS.buttonLabels.delete}
              disabled={!hasDeletePermission}
              icon={Icons.Delete}
              onClick={() => setOpenDeleteModal(true)}
              dataCy="editOrgDelete-"
            />
          </div>
        </div>
        <div className="panel-content">
          {dataLoading ? (
            <InlineLoader
              description={LOADING_TEXT_TRANSLATIONS.defaultLoadingText}
            />
          ) : (
            <div className="data-tabs">
              <div>
                {orgDataLoading ? (
                  <InlineLoader
                    description={LOADING_TEXT_TRANSLATIONS.orgLoadingText}
                  />
                ) : (
                  <div className="inner-datacontent mt-5">
                    <EditGroupFormButtons
                      panelName={header}
                      loading={orgDataLoading}
                      isEditing={isOrgEditing}
                      setIsEditing={setIsOrgEditing}
                      handleUpdate={handleOrgUpdate}
                      hasEditPermission={hasOrgEditPermission}
                      revertToInitialState={() => setOpenEditModal(true)}
                      isFormInvalid={isFormInvalid({
                        data: orgData,
                        formFields: orgFormFields,
                      })}
                    />
                    {!hasOrgError ? (
                      <EditRowFormFields
                        data={orgData}
                        isEditing={isOrgEditing}
                        esRef={esRef}
                        setData={setOrgData}
                        formFields={orgFormFields}
                        formModule={panelName}
                      />
                    ) : null}
                  </div>
                )}
              </div>
              {
                // eslint-disable-next-line no-nested-ternary
                orgProfileDataLoading ? (
                  <InlineLoader
                    description={
                      LOADING_TEXT_TRANSLATIONS.orgProfileLoadingText
                    }
                  />
                ) : (
                  // eslint-disable-next-line react/jsx-no-useless-fragment
                  <>
                    {hasOrgProfileViewPermission ? (
                      <div className="inner-datacontent mt-5">
                        <EditGroupFormButtons
                          panelName={
                            EDIT_ORG_PROFILE_TRANSLATIONS.profilePanelName
                          }
                          loading={orgProfileDataLoading}
                          isEditing={isOrgProfileEditing}
                          setIsEditing={setIsOrgProfileEditing}
                          handleUpdate={handleOrgProfileUpdate}
                          hasEditPermission={hasOrgProfileEditPermission}
                          revertToInitialState={() =>
                            setOpenEditProfileModal(true)
                          }
                          isFormInvalid={isFormInvalid({
                            data: orgProfileData,
                            formFields: orgProfileFormFields,
                          })}
                        />
                        {!hasOrgProfileError ? (
                          <EditRowFormFields
                            data={orgProfileData}
                            isEditing={isOrgProfileEditing}
                            esRef={esRef}
                            setData={setOrgProfileData}
                            formFields={orgProfileFormFields}
                            formModule={panelName}
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </>
                )
              }
            </div>
          )}
        </div>
      </aside>
      <DeleteModal
        modalHeaderText={EDIT_ORG_PROFILE_TRANSLATIONS.cancelOrgModal.header(
          header
        )}
        modalDescriptionText={
          EDIT_ORG_PROFILE_TRANSLATIONS.cancelOrgModal.description
        }
        onDelete={() => {
          setIsOrgEditing(false);
          setOrgData(initialOrgData);
          setOpenEditModal(false);
        }}
        open={openEditModal}
        setOpen={setOpenEditModal}
        confirmationButtonLabel={
          EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.confirmationButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <DeleteModal
        modalHeaderText={EDIT_ORG_PROFILE_TRANSLATIONS.cancelOrgProfileModal.header(
          header
        )}
        modalDescriptionText={
          EDIT_ORG_PROFILE_TRANSLATIONS.cancelOrgProfileModal.description
        }
        onDelete={() => {
          setIsOrgProfileEditing(false);
          setOrgProfileData(initialOrgProfileData);
          setOpenEditProfileModal(false);
        }}
        open={openEditProfileModal}
        setOpen={setOpenEditProfileModal}
        confirmationButtonLabel={
          EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.confirmationButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <DeleteModal
        modalHeaderText={EDIT_ORG_PROFILE_TRANSLATIONS.deleteOrgModal.header(
          header
        )}
        modalDescriptionText={EDIT_ORG_PROFILE_TRANSLATIONS.deleteOrgModal.description(
          header,
          orgData?.[deleteModalItemName]
        )}
        onDelete={() => onDelete(esRef)}
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
      />
    </>
  );
};

export default EditOrg;
